import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ShoppingProvider } from './context/ShoppingContext';
import { ToastProvider } from './context/ToastContext';
import { OrganizationSchema } from './components/SEO';
import Header from './components/Header';
import Footer from './components/Footer';

// Routes
import SellerRoutes from './routes/SellerRoutes';

// Pages
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Wishlist from './pages/Wishlist';
import Account from './pages/Account';
import Orders from './pages/Orders';
import GiftCards from './pages/GiftCards';
import Contact from './pages/Contact';
import Checkout from './pages/Checkout';
import OrderSuccess from './pages/OrderSuccess';
import AdminDashboard from './pages/AdminDashboard';
import AdminProducts from './pages/AdminProducts';
import AdminOrders from './pages/AdminOrders';
import AdminTrending from './pages/AdminTrending';
import AdminProductForm from './pages/AdminProductForm';
import AdminLogin from './pages/AdminLogin';
import AdminForgotPassword from './pages/AdminForgotPassword';
import AdminResetPassword from './pages/AdminResetPassword';
import AdminTeam from './pages/AdminTeam';
import AdminCoupons from './pages/AdminCoupons';
import AdminShipments from './pages/AdminShipments';
import AdminQuickTracking from './pages/AdminQuickTracking';
import AdminCategories from './pages/AdminCategories';
import AdminRoute from './components/AdminRoute';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import RefundPolicy from './pages/RefundPolicy';
import ShippingPolicy from './pages/ShippingPolicy';
import ShopCategory from './pages/ShopCategory';
import LiveTracking from './pages/LiveTracking';

// Styles
import './styles/globals.css';
import './App.css';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function AppContent() {
  const location = useLocation();
  const isSellerRoute = location.pathname.startsWith('/seller');

  return (
    <div className="app">
      <ScrollToTop />
      {!isSellerRoute && <Header />}
      <main className="main-content" style={isSellerRoute ? { paddingTop: 0 } : {}}>
        <Routes>
          {/* Customer Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/shop/:category" element={<ShopCategory />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/orders" element={<Orders />} />
          <Route path="/gift-cards" element={<GiftCards />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/account" element={<Account />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/order-success" element={<OrderSuccess />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/shipping-policy" element={<ShippingPolicy />} />
          <Route path="/track/:orderId" element={<LiveTracking />} />

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
          <Route path="/admin/reset-password" element={<AdminResetPassword />} />
          <Route path="/admin" element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          } />
          <Route path="/admin/products" element={
            <AdminRoute>
              <AdminProducts />
            </AdminRoute>
          } />
          <Route path="/admin/orders" element={
            <AdminRoute>
              <AdminOrders />
            </AdminRoute>
          } />
          <Route path="/admin/trending" element={
            <AdminRoute>
              <AdminTrending />
            </AdminRoute>
          } />
          <Route path="/admin/add" element={
            <AdminRoute>
              <AdminProductForm />
            </AdminRoute>
          } />
          <Route path="/admin/team" element={
            <AdminRoute>
              <AdminTeam />
            </AdminRoute>
          } />
          <Route path="/admin/coupons" element={
            <AdminRoute>
              <AdminCoupons />
            </AdminRoute>
          } />
          <Route path="/admin/shipments" element={
            <AdminRoute>
              <AdminShipments />
            </AdminRoute>
          } />
          <Route path="/admin/categories" element={
            <AdminRoute>
              <AdminCategories />
            </AdminRoute>
          } />
          <Route path="/admin/quick-tracking" element={
            <AdminRoute>
              <AdminQuickTracking />
            </AdminRoute>
          } />
          <Route path="/admin/edit/:id" element={
            <AdminRoute>
              <AdminProductForm />
            </AdminRoute>
          } />

          {/* Seller Routes */}
          <Route path="/seller/*" element={<SellerRoutes />} />
        </Routes>
      </main>
      {!isSellerRoute && <Footer />}
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <OrganizationSchema />
      <ToastProvider>
        <ShoppingProvider>
          <Router>
            <AppContent />
          </Router>
        </ShoppingProvider>
      </ToastProvider>
    </HelmetProvider>
  );
}

export default App;
